import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'tr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'tr' ? 'en' : 'tr');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  tr: {
    // Header
    header: {
      home: 'Ana Sayfa',
      about: 'Hakkımızda',
      purpose: 'Amacımız',
      trainings: 'Eğitimler',
      projects: 'Projeler',
      events: 'Etkinlikler',
      opportunities: 'Fırsatlar',
      contact: 'İletişim',
    },
    // Hero Section
    hero: {
      badge: 'Geleceğin Siber Güvenlik Uzmanları',
      title1: 'Siber Güvenlik',
      title2: 'Araştırmacıları ve Gençlik Derneği',
      subtitle: 'Batı Karadeniz bölgesindeki gençleri siber güvenlik, yazılım ve dijital becerilerle geleceğin uzmanları olarak yetiştiriyoruz.',
      aboutBtn: 'Hakkımızda',
      purposeBtn: 'Derneğin Amacı',
    },
    // Stats Section
    stats: {
      years: 'Yıllık Deneyim',
      youth: 'Eğitime Katılan Genç',
      startups: 'Girişim Sayısı',
    },
    // Features Section
    features: {
      badge: 'Hizmetlerimiz',
      title: 'Ne Yapıyoruz?',
      subtitle: 'Gençler için teknik beceri, kariyer ve uluslararası deneyim',
      trainings: {
        title: 'Eğitimler',
        description: 'Bilişim alanında kendini geliştirmek isteyen bireylerin donanımlı şekilde hazırlanmasını destekleyen eğitim programları sunuyoruz.',
      },
      projects: {
        title: 'Projeler',
        description: 'Gençler, gençlik çalışanları ve eğitmenler için düzenlenen gençlik değişimleri, eğitim kursları, çalıştaylar, hareketlilik ve öğrenme programları yürütüyoruz.',
      },
      events: {
        title: 'Etkinlikler',
        description: 'Topluluk oluşturma ve deneyim paylaşımı odaklı etkinliklerle gençlerin kişisel ve mesleki gelişimini destekliyoruz.',
      },
    },
    // Opportunities Section
    opportunities: {
      badge: 'Fırsatlar',
      title: 'Gençler İçin Fırsatlar',
      subtitle: 'Siber güvenlik ve teknoloji alanında kendinizi geliştirin',
      arge: {
        title: 'AR-GE Faaliyetleri',
        description: 'Gençlerle birlikte siber güvenlik odaklı araştırma, analiz ve prototip geliştirme çalışmaları yürütüyoruz.',
      },
      tech: {
        title: 'Teknolojik Yenilikler',
        description: 'Yapay zekâ, siber güvenlik ve dijital teknolojiler alanında gençlere yeni nesil beceriler kazandırıyoruz.',
      },
      innovative: {
        title: 'İnovatif Projeler',
        description: 'Gençlerin fikirlerini sosyal fayda odaklı, yenilikçi projelere ve prototiplere dönüştürmelerini destekliyoruz.',
      },
    },
    // News Section
    news: {
      badge: 'Güncel',
      title: 'Haberler ve Etkinlikler',
      subtitle: 'Derneğimizden en son haberler ve etkinlikler',
      readMore: 'Devamını Oku',
      news1: {
        title: 'Üniversite Öğrencileri İçin "Am I Safe?" Semineri Düzenlendi',
        excerpt: 'Projenin yaygınlaştırma faaliyetleri kapsamında, dijital teknolojiler alanında eğitim gören üniversite öğrencilerine yönelik "Am I Safe?" başlıklı bir seminer gerçekleştirildi.',
      },
      news2: {
        title: '"Kişisel Verileriniz Ne Kadar Güvende?" Projesi Kapsamında Konferans Düzenlendi',
        excerpt: 'Erasmus+ KA210-Youth programı kapsamında yürütülen proje çerçevesinde, kişisel veri güvenliği ve dijital farkındalık temalı bir konferans gerçekleştirildi.',
      },
      news3: {
        title: '"Güvende Miyim?" Platformu Liseli Gençlere Tanıtıldı',
        excerpt: 'Erasmus+ KA210 programı kapsamında geliştirilen "Güvende Miyim?" Platformu, liselerde eğitim gören gençlere tanıtıldı.',
      },
      news4: {
        title: 'Çok Uluslu Siber Güvenlik Platformu Geliştiriliyor',
        excerpt: 'Avrupa Birliği tarafından finanse edilen "How Safe Is Your Personal Data" projesi kapsamında çok uluslu bir siber güvenlik platformu hazırlanıyor.',
      },
    },
    // Contact Section
    contact: {
      badge: 'İletişim',
      title: 'Bize Ulaşın',
      subtitle: 'Sorularınız için bize ulaşabilirsiniz',
      infoTitle: 'İletişim Bilgileri',
      infoText: 'Gençlerin siber güvenlik alanındaki yolculuğuna eşlik etmek için buradayız. Bize ulaşmaktan çekinmeyin.',
      email: 'E-posta',
      location: 'Konum',
      formTitle: 'Mesaj Gönderin',
      name: 'Adınız',
      namePlaceholder: 'Adınızı girin',
      emailLabel: 'E-posta',
      emailPlaceholder: 'E-posta adresiniz',
      subject: 'Konu',
      subjectPlaceholder: 'Mesaj konusu',
      message: 'Mesajınız',
      messagePlaceholder: 'Mesajınızı yazın...',
      send: 'Mesaj Gönder',
      success: 'Mesajınız gönderildi!',
    },
    // Footer
    footer: {
      description: 'Siber Güvenlik Araştırmacıları ve Gençlik Derneği - Gençleri geleceğin siber güvenlik uzmanları olarak yetiştiriyoruz.',
      quickLinks: 'Hızlı Bağlantılar',
      contact: 'İletişim',
      rights: 'Tüm hakları saklıdır.',
    },
    // ==================== SUBPAGES ====================
    // About Page
    about: {
      badge: 'Hakkımızda',
      title: 'Biz Kimiz?',
      intro1: 'Siber Güvenlik Araştırmacıları ve Gençlik Derneği; Zonguldak ve Batı Karadeniz Bölgesi\'nde gençlerin dijital dünyada güvenli, üretken ve sorumlu bireyler olarak yer almasını destekleyen, teknoloji ve gençlik odaklı bir sivil toplum kuruluşudur.',
      intro2: 'Siber güvenlik ve bilişim teknolojileri alanındaki çalışmalarımızın yanı sıra; gençlik çalışmaları, girişimciliği destekleme, çevre ve ekoloji bilinci, sürdürülebilir yaşam, kültürler arası etkileşim ve toplumsal sorumluluk temelli faaliyetler yürütüyoruz.',
      intro3: 'Eğitim programları, projeler, atölyeler ve topluluk etkinlikleri ile gençlerin yalnızca teknik becerilerini değil; aynı zamanda yaratıcılık, dayanışma, çevre duyarlılığı ve sosyal farkındalık gibi güçlü yönlerini de geliştirmeyi hedefliyoruz.',
      intro4: 'Amacımız, teknolojiyi merkezine alan fakat insanı, doğayı ve geleceği göz ardı etmeyen; öğrenen, paylaşan, üreten ve birlikte güçlenen bir topluluk inşa etmektir.',
      valuesTitle: 'Değerlerimiz',
      values: {
        cyber: { title: 'Siber Güvenlik', desc: 'Dijital dünyada güvenli bireyler yetiştiriyoruz' },
        youth: { title: 'Gençlik Odaklı', desc: 'Gençlerin potansiyelini açığa çıkarıyoruz' },
        environment: { title: 'Çevre Bilinci', desc: 'Sürdürülebilir gelecek için çalışıyoruz' },
        global: { title: 'Kültürler Arası', desc: 'Uluslararası iş birliği ve etkileşim' },
        entrepreneurship: { title: 'Girişimcilik', desc: 'Yenilikçi fikirleri destekliyoruz' },
        social: { title: 'Toplumsal Sorumluluk', desc: 'Topluma değer katıyoruz' },
      },
    },
    // Purpose Page
    purpose: {
      badge: 'Misyonumuz',
      title: 'Derneğin Amacı',
      intro1: 'Siber Güvenlik Araştırmacıları ve Gençlik Derneği olarak amacımız; ulusal ve uluslararası düzeyde faaliyet gösteren, tarafsız, güvenilir ve etkili bir sivil toplum kuruluşu olarak toplumun dijital geleceğine katkı sunmaktır.',
      intro2: 'Bölgemizde bu alanlarda çalışan insan kaynağını artırarak istihdamı destekliyor, teknik, bilimsel, sosyal ve kültürel projelerle bilişim ekosisteminin gelişimine katkı sağlıyoruz. Üyelerimizin mesleki gelişimini destekleyen programlar geliştiriyor; öğrenen, üreten ve paylaşan bir topluluk kültürü oluşturuyoruz.',
      intro3: 'Bu yolculukta; bilgiyi paylaşan, birlikte üreten ve geleceği dönüştüren bir topluluk olmayı amaçlıyoruz.',
      techAreasTitle: 'Odaklandığımız Teknoloji Alanları',
      socialTitle: 'Toplumsal Sorumluluk Alanları',
      socialSubtitle: 'Yalnızca teknolojiye değil; insana, toplumsal değerlere ve yaşadığımız dünyaya da sorumlulukla yaklaşıyoruz.',
      social: {
        environment: { title: 'Çevre Duyarlılığı', desc: 'Küresel ısınma ve geri dönüşüm farkındalığı' },
        intercultural: { title: 'Kültürler Arası Etkileşim', desc: 'Uluslararası iş birliği ve deneyim paylaşımı' },
        youth: { title: 'Sağlıklı Gençlik', desc: 'Bilinçli ve kapsayıcı topluluk oluşturma' },
      },
    },
    // Trainings Page
    trainings: {
      badge: 'Eğitim Programları',
      title: 'Eğitimler',
      intro1: 'Siber Güvenlik Araştırmacıları ve Gençlik Derneği olarak, gençlerin ve bilişim alanında kendini geliştirmek isteyen bireylerin geleceğin mesleklerine donanımlı şekilde hazırlanmasını destekleyen eğitim programları geliştiriyoruz.',
      intro2: 'Eğitimlerimiz; siber güvenlik, yapay zekâ, yazılım geliştirme, dijital okuryazarlık, girişimcilik, çevre ve sürdürülebilirlik odaklı teknoloji uygulamaları gibi geniş bir alanda yürütülmektedir.',
      intro3: 'Programlarımız teori ile sınırlı kalmaz; uygulamalı atölyeler, CTF tabanlı öğrenme ortamları, proje geliştirme çalışmaları, mentorluk desteği ve takım temelli öğrenme yaklaşımlarıyla desteklenir.',
      categories: {
        cyber: {
          title: 'Siber Güvenlik Eğitimleri',
          items: ['Temel & ileri seviye siber güvenlik', 'Ağ güvenliği ve Linux temelleri', 'Web güvenliği ve sızma testi yaklaşımları', 'Malware analizi ve zararlı yazılım farkındalığı', 'OSINT ve dijital iz analizi', 'CTF ve uygulamalı senaryo temelli eğitimler'],
        },
        ai: {
          title: 'Yapay Zekâ ve Yeni Nesil Teknolojiler',
          items: ['Yapay zekâ farkındalığı ve etik kullanımı', 'Prompt mühendisliği & generatif AI uygulamaları', 'Veri okuryazarlığı ve analitik bakış', 'AI destekli üretkenlik araçları', 'Dijital yaratıcı üretim becerileri'],
        },
        software: {
          title: 'Yazılım ve Geliştirici Becerileri',
          items: ['Temel programlama mantığı', 'Web teknolojileri ve güvenli kodlama farkındalığı', 'Mobil ve oyun geliştirme odaklı giriş eğitimleri', 'Proje geliştirme ve pratik öğrenme çalışmaları'],
        },
        social: {
          title: 'Sosyal Etki & Sürdürülebilirlik Odaklı Eğitimler',
          items: ['Dijital dünyada etik ve sorumlu vatandaşlık', 'Çevre bilinci ve sürdürülebilir teknoloji yaklaşımı', 'Girişimcilik ve yenilikçi düşünme', 'Takım çalışması, liderlik ve topluluk aidiyeti'],
        },
      },
      approachTitle: 'Eğitim Yaklaşımımız',
      approachSubtitle: '"Ezber değil, deneyim odaklı öğrenme."',
      approach: ['Uygulamalı öğrenme', 'Gerçek hayat senaryoları', 'Üretim ve proje odaklı süreç', 'Mentorluk ve rehberlik desteği', 'Topluluk temelli öğrenme kültürü'],
    },
    // Projects Page
    projects: {
      badge: 'Erasmus+ Projeleri',
      title: 'Projeler',
      intro: 'Derneğimiz, siber güvenlik, bilişim teknolojileri, dijital beceriler, gençlik çalışmaları ve sosyal yenilik alanlarında yürüttüğü faaliyetler doğrultusunda Erasmus+ KA1 ve KA2 projelerinde aktif olarak yer alabilecek kapasite ve deneyime sahiptir.',
      ka1: {
        title: 'KA1 – Bireylerin Öğrenme Hareketliliği',
        description: 'Derneğimiz; gençler, gençlik çalışanları ve eğitmenler için düzenlenen gençlik değişimleri, eğitim kursları, çalıştaylar, hareketlilik ve öğrenme programlarında katılımcı veya ortak kurum olarak görev alabilmektedir. Bu kapsamda, gençlerin yeni beceriler kazanmasını, kültürler arası öğrenme deneyimi yaşamasını ve uluslararası iş birliği kültürü geliştirmesini destekliyoruz.',
        features: ['Gençlik değişimleri', 'Eğitim kursları', 'Çalıştaylar', 'Hareketlilik programları', 'Öğrenme programları', 'Kültürler arası öğrenme'],
      },
      ka2: {
        title: 'KA2 – İş Birliği Ortaklıkları',
        description: 'KA2 projeleri kapsamında; sivil toplum kuruluşları, üniversiteler, kamu kurumları, teknoloji firmaları ve eğitim kuruluşları ile birlikte yenilikçi eğitim içerikleri, dijital öğrenme araçları, araştırma çıktıları ve sosyal etki odaklı projeler geliştirmekte ve uygulamaktayız.',
        partners: ['Sivil Toplum Kuruluşları', 'Üniversiteler', 'Kamu Kurumları', 'Teknoloji Firmaları', 'Eğitim Kuruluşları'],
        outcomes: ['Yenilikçi eğitim içerikleri', 'Dijital öğrenme araçları', 'Araştırma çıktıları', 'Sosyal etki odaklı projeler', 'Dijital yetkinliklerin güçlendirilmesi', 'Siber güvenlik farkındalığının artırılması'],
      },
      vision: 'Erasmus+ iş birlikleri aracılığıyla, sürdürülebilir ortaklıklar kurmayı, bilgi ve deneyim paylaşımını teşvik etmeyi ve gençlerin aktif, donanımlı ve sorumlu bireyler olarak topluma katkı sunmalarını desteklemeyi amaçlıyoruz.',
    },
    // Events Page
    events: {
      badge: 'Topluluk Faaliyetleri',
      title: 'Etkinlikler',
      intro1: 'Derneğimiz, yalnızca eğitim faaliyetleriyle değil; aynı zamanda bilinçlendirme, topluluk oluşturma ve deneyim paylaşımı odaklı etkinliklerle de gençlerin gelişimini desteklemektedir.',
      intro2: 'Bu doğrultuda; ağımızı güçlendiren, yeni bakış açıları kazandıran ve katılımcılara ilham veren seminerler, atölyeler, konferanslar, buluşmalar ve öğrenme odaklı sosyal etkinlikler düzenliyoruz.',
      intro3: 'Her etkinlikte bilgi paylaşımını teşvik ederken, gençlerin aktif katılımını ve birlikte üretme kültürünü ön planda tutuyoruz.',
      typesTitle: 'Etkinlik Türlerimiz',
      seminars: { title: 'Seminerler', desc: 'Uzman konuşmacılarla bilgi paylaşımı' },
      workshops: { title: 'Atölyeler', desc: 'Uygulamalı öğrenme deneyimleri' },
      conferences: { title: 'Konferanslar', desc: 'Gençlerimizi güncel bilgi, deneyim ve ilhamla buluşturuyoruz' },
      visionMeetings: { title: 'Vizyon Buluşmaları', desc: 'Sektörün önde gelenleri ile buluşturuyoruz' },
      socialEvents: { title: 'Sosyal Etkinlikler', desc: 'Topluluk bilinci kazanılan sosyal ve kültürel etkinlikler' },
      competitions: { title: 'Yarışmalar', desc: 'Problem çözme, takım çalışması ve rekabetçi öğrenme deneyimi' },
      visionTitle: 'Vizyonumuz',
      visionText: 'Gençlere ilham veren, öğrenmeyi ve etkileşimi merkeze alan yenilikçi deneyimler sunmaktır.',
    },
    // Opportunities Page
    opportunitiesPage: {
      badge: 'Kariyer Fırsatları',
      title: 'Gençler İçin Fırsatlar',
      subtitle: 'Siber güvenlik ve teknoloji alanında kendinizi geliştirin, geleceğin uzmanları arasında yerinizi alın.',
      arge: {
        title: 'AR-GE Faaliyetleri',
        subtitle: 'Araştırma ve Geliştirme',
        intro1: 'Derneğimiz bünyesinde yürütülen AR-GE faaliyetleri, gençlerin uygulamalı öğrenme, analiz yapma, üretken düşünme ve problem çözme becerilerini geliştirmeyi hedeflemektedir.',
        intro2: 'Bu çalışmalar, gençlerin yalnızca bilgi tüketen bireyler olmaktan çıkarak, gerçek dünya odaklı çözümler üreten ve katma değer yaratan genç araştırmacılar haline gelmelerini sağlar.',
        features: ['Güvenlik açıkları analizi', 'Siber tehdit senaryolarının modellenmesi', 'Veri odaklı çözüm geliştirme', 'Prototip üretimi', 'Üretken düşünme', 'Tehdit modelleme', 'Veri analizi', 'Uygulamalı AR-GE süreçleri'],
        quote: '"Geleceğin siber güvenlik çözümlerini gençlerle birlikte geliştiriyoruz."',
      },
      tech: {
        title: 'Teknolojik Yenilikler',
        subtitle: 'Geleceğin Becerileri',
        intro1: 'Derneğimiz, gençlerin teknolojiyi yalnızca tüketen değil; üreten, sorgulayan ve dönüştüren bireyler olarak yetişmesini hedeflemektedir.',
        intro2: 'Programlarımızda gençler, simülasyon tabanlı öğrenme ortamları ve gerçekçi senaryo uygulamaları sayesinde karmaşık teknolojik süreçleri deneyimleyerek öğrenir.',
        areas: ['Yapay zekâ', 'Siber güvenlik', 'Veri analizi', 'Oyun ve simülasyon teknolojileri'],
        activities: ['Uygulamalı laboratuvar çalışmaları', 'Proje tabanlı öğrenme görevleri', 'Prototip ve demo geliştirme çalışmaları', 'Disiplinler arası teknoloji üretim süreçleri'],
        skills: ['Yaratıcılık', 'İnovatif düşünme', 'Takım çalışması', 'Sorumluluk bilinci'],
        conclusion: 'Gençler, dijital dünyanın pasif kullanıcıları olmaktan çıkar ve teknolojiyi şekillendiren aktif üreticiler haline gelir.',
      },
      innovative: {
        title: 'İnovatif Projeler',
        subtitle: 'Girişimcilik Odaklı Öğrenme',
        intro1: 'Derneğimiz, gençlerin sosyal fayda odaklı, teknoloji temelli ve yenilikçi proje fikirleri geliştirmelerini destekleyen bütüncül bir öğrenme ekosistemi sunmaktadır.',
        intro2: 'Fikirden ürüne uzanan bu süreçte gençlere yalnızca teknik bilgi değil, yaratıcılık, girişimcilik ve sorumluluk bilinci kazandırmayı hedefliyoruz.',
        steps: ['Problem analizi', 'İhtiyaç belirleme', 'Çözüm tasarımı', 'Prototip geliştirme'],
        outcomes: ['Prototip ve demo üretimi', 'Kullanıcı geri bildirimi toplama', 'İyileştirme ve yeniden tasarım', 'Temel sunum ve proje yönetimi uygulamaları'],
        conclusion: 'Katılımcılar, fikirlerini gerçek dünyada değer üreten çözümlere dönüştürme konusunda yetkin ve donanımlı bireyler haline gelir.',
      },
      cta: {
        title: 'Geleceğin Siber Güvenlik Uzmanı Ol',
        description: 'Derneğimizin sunduğu fırsatlardan yararlanarak kariyerine güçlü bir başlangıç yap.',
        button: 'Bize Ulaşın',
      },
    },
  },
  en: {
    // Header
    header: {
      home: 'Home',
      about: 'About Us',
      purpose: 'Our Mission',
      trainings: 'Training Programs',
      projects: 'Projects',
      events: 'Events',
      opportunities: 'Opportunities',
      contact: 'Contact',
    },
    // Hero Section
    hero: {
      badge: 'Future Cybersecurity Experts',
      title1: 'Cybersecurity',
      title2: 'Researchers and Youth Association',
      subtitle: 'We empower young people in the Western Black Sea region as future experts through cybersecurity, software development, and digital skills training.',
      aboutBtn: 'About Us',
      purposeBtn: 'Our Mission',
    },
    // Stats Section
    stats: {
      years: 'Years of Experience',
      youth: 'Youth Trained',
      startups: 'Initiatives Launched',
    },
    // Features Section
    features: {
      badge: 'Our Services',
      title: 'What We Do',
      subtitle: 'Technical skills, career development, and international experience for youth',
      trainings: {
        title: 'Training Programs',
        description: 'We provide comprehensive training programs designed to equip individuals seeking to advance their careers in information technology with essential skills and knowledge.',
      },
      projects: {
        title: 'Projects',
        description: 'We implement youth exchanges, training courses, workshops, mobility programs, and learning initiatives for young people, youth workers, and educators.',
      },
      events: {
        title: 'Events',
        description: 'We support the personal and professional development of young people through community-building events and experience-sharing activities.',
      },
    },
    // Opportunities Section
    opportunities: {
      badge: 'Opportunities',
      title: 'Opportunities for Youth',
      subtitle: 'Develop your expertise in cybersecurity and emerging technologies',
      arge: {
        title: 'R&D Activities',
        description: 'We conduct cybersecurity-focused research, analysis, and prototype development initiatives in collaboration with young researchers.',
      },
      tech: {
        title: 'Technological Innovation',
        description: 'We equip young people with next-generation competencies in artificial intelligence, cybersecurity, and digital technologies.',
      },
      innovative: {
        title: 'Innovative Projects',
        description: 'We support young people in transforming their ideas into socially beneficial, innovative projects and working prototypes.',
      },
    },
    // News Section
    news: {
      badge: 'Latest News',
      title: 'News and Events',
      subtitle: 'Stay updated with our latest activities and announcements',
      readMore: 'Read More',
      news1: {
        title: '"Am I Safe?" Seminar Organized for University Students',
        excerpt: 'As part of project dissemination activities, an "Am I Safe?" seminar was conducted for university students studying digital technologies.',
      },
      news2: {
        title: 'Conference Held Within "How Safe Is Your Personal Data?" Project',
        excerpt: 'A conference on personal data security and digital awareness was organized within the framework of the Erasmus+ KA210-Youth program.',
      },
      news3: {
        title: '"Am I Safe?" Platform Introduced to High School Students',
        excerpt: 'The "Am I Safe?" Platform, developed under the Erasmus+ KA210 program, was presented to high school students.',
      },
      news4: {
        title: 'Multinational Cybersecurity Platform Under Development',
        excerpt: 'A multinational cybersecurity platform is being developed within the EU-funded "How Safe Is Your Personal Data" project.',
      },
    },
    // Contact Section
    contact: {
      badge: 'Contact',
      title: 'Get in Touch',
      subtitle: 'We welcome your inquiries and collaboration proposals',
      infoTitle: 'Contact Information',
      infoText: 'We are committed to supporting young people on their journey in cybersecurity. Please do not hesitate to reach out to us.',
      email: 'Email',
      location: 'Location',
      formTitle: 'Send a Message',
      name: 'Your Name',
      namePlaceholder: 'Enter your full name',
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email address',
      subject: 'Subject',
      subjectPlaceholder: 'Message subject',
      message: 'Your Message',
      messagePlaceholder: 'Write your message here...',
      send: 'Send Message',
      success: 'Your message has been sent successfully!',
    },
    // Footer
    footer: {
      description: 'Cybersecurity Researchers and Youth Association - Empowering young people as future cybersecurity experts.',
      quickLinks: 'Quick Links',
      contact: 'Contact',
      rights: 'All rights reserved.',
    },
    // ==================== SUBPAGES ====================
    // About Page
    about: {
      badge: 'About Us',
      title: 'Who We Are',
      intro1: 'The Cybersecurity Researchers and Youth Association is a technology and youth-focused non-governmental organization dedicated to supporting young people in Zonguldak and the Western Black Sea Region to become safe, productive, and responsible digital citizens.',
      intro2: 'In addition to our work in cybersecurity and information technologies, we conduct activities focused on youth development, entrepreneurship support, environmental and ecological awareness, sustainable living, intercultural exchange, and social responsibility.',
      intro3: 'Through educational programs, projects, workshops, and community events, we aim to develop not only the technical skills of young people but also their creativity, solidarity, environmental sensitivity, and social awareness.',
      intro4: 'Our mission is to build a community that places technology at its center while never losing sight of humanity, nature, and the future—a community that learns, shares, creates, and grows stronger together.',
      valuesTitle: 'Our Core Values',
      values: {
        cyber: { title: 'Cybersecurity', desc: 'Cultivating digitally secure individuals' },
        youth: { title: 'Youth-Centered', desc: 'Unlocking the potential of young people' },
        environment: { title: 'Environmental Awareness', desc: 'Working towards a sustainable future' },
        global: { title: 'Intercultural Exchange', desc: 'Fostering international collaboration' },
        entrepreneurship: { title: 'Entrepreneurship', desc: 'Supporting innovative ideas' },
        social: { title: 'Social Responsibility', desc: 'Creating value for society' },
      },
    },
    // Purpose Page
    purpose: {
      badge: 'Our Mission',
      title: 'Our Purpose',
      intro1: 'As the Cybersecurity Researchers and Youth Association, our purpose is to contribute to the digital future of society as a neutral, reliable, and effective non-governmental organization operating at both national and international levels.',
      intro2: 'We support employment by increasing the human capital working in these fields in our region, and contribute to the development of the IT ecosystem through technical, scientific, social, and cultural projects. We develop programs that support the professional development of our members, creating a community culture of learning, producing, and sharing.',
      intro3: 'On this journey, we aim to be a community that shares knowledge, creates together, and transforms the future.',
      techAreasTitle: 'Technology Focus Areas',
      socialTitle: 'Social Responsibility Areas',
      socialSubtitle: 'We approach not only technology but also people, social values, and the world we live in with a sense of responsibility.',
      social: {
        environment: { title: 'Environmental Sensitivity', desc: 'Climate change and recycling awareness' },
        intercultural: { title: 'Intercultural Exchange', desc: 'International cooperation and experience sharing' },
        youth: { title: 'Youth Well-being', desc: 'Building conscious and inclusive communities' },
      },
    },
    // Trainings Page
    trainings: {
      badge: 'Training Programs',
      title: 'Educational Programs',
      intro1: 'As the Cybersecurity Researchers and Youth Association, we develop comprehensive training programs designed to prepare young people and individuals seeking to advance in information technology for the careers of the future.',
      intro2: 'Our training programs span a wide range of areas including cybersecurity, artificial intelligence, software development, digital literacy, entrepreneurship, and sustainability-focused technology applications.',
      intro3: 'Our programs go beyond theory; they are supported by hands-on workshops, CTF-based learning environments, project development activities, mentorship support, and team-based learning approaches.',
      categories: {
        cyber: {
          title: 'Cybersecurity Training',
          items: ['Foundational & advanced cybersecurity', 'Network security and Linux fundamentals', 'Web security and penetration testing approaches', 'Malware analysis and threat awareness', 'OSINT and digital forensics', 'CTF and scenario-based practical training'],
        },
        ai: {
          title: 'Artificial Intelligence & Emerging Technologies',
          items: ['AI awareness and ethical usage', 'Prompt engineering & generative AI applications', 'Data literacy and analytical thinking', 'AI-powered productivity tools', 'Digital creative production skills'],
        },
        software: {
          title: 'Software Development Skills',
          items: ['Programming fundamentals', 'Web technologies and secure coding practices', 'Mobile and game development introductory courses', 'Project development and practical learning'],
        },
        social: {
          title: 'Social Impact & Sustainability Programs',
          items: ['Digital ethics and responsible citizenship', 'Environmental awareness and sustainable technology', 'Entrepreneurship and innovative thinking', 'Teamwork, leadership, and community belonging'],
        },
      },
      approachTitle: 'Our Educational Approach',
      approachSubtitle: '"Experience-based learning, not memorization."',
      approach: ['Hands-on learning', 'Real-world scenarios', 'Project-based processes', 'Mentorship and guidance', 'Community-based learning culture'],
    },
    // Projects Page
    projects: {
      badge: 'Erasmus+ Projects',
      title: 'Our Projects',
      intro: 'Our association possesses the capacity and experience to actively participate in Erasmus+ KA1 and KA2 projects in line with our activities in cybersecurity, information technologies, digital skills, youth work, and social innovation.',
      ka1: {
        title: 'KA1 – Learning Mobility of Individuals',
        description: 'Our association can serve as a participant or partner organization in youth exchanges, training courses, workshops, mobility programs, and learning activities organized for young people, youth workers, and educators. In this context, we support young people in acquiring new skills, experiencing intercultural learning, and developing a culture of international cooperation.',
        features: ['Youth exchanges', 'Training courses', 'Workshops', 'Mobility programs', 'Learning activities', 'Intercultural learning'],
      },
      ka2: {
        title: 'KA2 – Cooperation Partnerships',
        description: 'Within the scope of KA2 projects, we develop and implement innovative educational content, digital learning tools, research outputs, and social impact-focused projects in collaboration with civil society organizations, universities, public institutions, technology companies, and educational organizations.',
        partners: ['Civil Society Organizations', 'Universities', 'Public Institutions', 'Technology Companies', 'Educational Organizations'],
        outcomes: ['Innovative educational content', 'Digital learning tools', 'Research outputs', 'Social impact projects', 'Digital competency enhancement', 'Cybersecurity awareness development'],
      },
      vision: 'Through Erasmus+ partnerships, we aim to establish sustainable collaborations, promote knowledge and experience sharing, and support young people in contributing to society as active, equipped, and responsible individuals.',
    },
    // Events Page
    events: {
      badge: 'Community Activities',
      title: 'Events',
      intro1: 'Our association supports the development of young people not only through educational activities but also through awareness-raising, community-building, and experience-sharing events.',
      intro2: 'In this direction, we organize seminars, workshops, conferences, networking sessions, and learning-focused social events that strengthen our network, provide new perspectives, and inspire participants.',
      intro3: 'At every event, we encourage knowledge sharing while prioritizing the active participation of young people and fostering a culture of collaborative creation.',
      typesTitle: 'Event Categories',
      seminars: { title: 'Seminars', desc: 'Knowledge sharing with expert speakers' },
      workshops: { title: 'Workshops', desc: 'Hands-on learning experiences' },
      conferences: { title: 'Conferences', desc: 'Connecting youth with current knowledge, experience, and inspiration' },
      visionMeetings: { title: 'Vision Meetings', desc: 'Networking with industry leaders' },
      socialEvents: { title: 'Social Events', desc: 'Social and cultural activities fostering community awareness' },
      competitions: { title: 'Competitions', desc: 'Problem-solving, teamwork, and competitive learning experiences' },
      visionTitle: 'Our Vision',
      visionText: 'To provide innovative experiences that inspire young people and place learning and interaction at the center.',
    },
    // Opportunities Page
    opportunitiesPage: {
      badge: 'Career Opportunities',
      title: 'Opportunities for Youth',
      subtitle: 'Develop your expertise in cybersecurity and technology, and secure your place among future experts.',
      arge: {
        title: 'R&D Activities',
        subtitle: 'Research and Development',
        intro1: 'The R&D activities conducted within our association aim to develop young people\'s skills in applied learning, analysis, productive thinking, and problem-solving.',
        intro2: 'These activities enable young people to transition from being mere consumers of information to becoming young researchers who produce real-world solutions and create added value.',
        features: ['Security vulnerability analysis', 'Cyber threat scenario modeling', 'Data-driven solution development', 'Prototype production', 'Productive thinking', 'Threat modeling', 'Data analysis', 'Applied R&D processes'],
        quote: '"We develop the cybersecurity solutions of the future together with young people."',
      },
      tech: {
        title: 'Technological Innovation',
        subtitle: 'Skills for the Future',
        intro1: 'Our association aims to cultivate young people who not only consume technology but also create, question, and transform it.',
        intro2: 'In our programs, young people learn complex technological processes through simulation-based learning environments and realistic scenario applications.',
        areas: ['Artificial Intelligence', 'Cybersecurity', 'Data Analysis', 'Gaming and Simulation Technologies'],
        activities: ['Applied laboratory work', 'Project-based learning tasks', 'Prototype and demo development', 'Interdisciplinary technology production processes'],
        skills: ['Creativity', 'Innovative thinking', 'Teamwork', 'Sense of responsibility'],
        conclusion: 'Young people move beyond being passive users of the digital world to become active creators who shape technology.',
      },
      innovative: {
        title: 'Innovative Projects',
        subtitle: 'Entrepreneurship-Focused Learning',
        intro1: 'Our association provides a holistic learning ecosystem that supports young people in developing socially beneficial, technology-based, and innovative project ideas.',
        intro2: 'Throughout this journey from idea to product, we aim to equip young people not only with technical knowledge but also with creativity, entrepreneurship, and a sense of responsibility.',
        steps: ['Problem analysis', 'Needs assessment', 'Solution design', 'Prototype development'],
        outcomes: ['Prototype and demo production', 'User feedback collection', 'Improvement and redesign', 'Basic presentation and project management practices'],
        conclusion: 'Participants become competent and equipped individuals capable of transforming their ideas into real-world value-creating solutions.',
      },
      cta: {
        title: 'Become a Future Cybersecurity Expert',
        description: 'Take advantage of the opportunities offered by our association and make a strong start to your career.',
        button: 'Contact Us',
      },
    },
  },
};
